import type { MutableRefObject } from 'react';
import { useLayoutEffect, useRef } from 'react';

/**
 * Returns a ref that always contains the latest value.
 *
 * Solves the "stale closure" problem for callbacks assigned to browser APIs
 * (MediaRecorder, WebSocket, etc.) that are set once and never updated.
 *
 * @example
 * ```tsx
 * const onMessageRef = useLatest(onMessage);
 *
 * useEffect(() => {
 *   const ws = new WebSocket(url);
 *   ws.onmessage = (e) => onMessageRef.current?.(e.data); // Always calls latest
 *   return () => ws.close();
 * }, [url]);
 * ```
 *
 * **Use when:** Event handlers are assigned once (MediaRecorder, WebSocket, Observers)
 *
 * **Don't use when:** Values are used in render, or effects should re-run on change
 *
 * **ESLint:** Add `// eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest ref is stable`
 * @param value - The value to keep current
 * @returns A ref whose `.current` always holds the latest value
 *
 * ---
 * @remarks
 * ## Extended Documentation (for detailed review or AI agents)
 *
 * ### Good Use Cases
 *
 * **Browser API event handlers assigned once:**
 * ```tsx
 * const onChunkRef = useLatest(onChunk);
 * const recorder = useMemo(() => {
 *   const r = new MediaRecorder(stream);
 *   r.ondataavailable = (e) => onChunkRef.current?.(e.data);
 *   return r;
 * }, [stream]); // onChunk not needed in deps
 * ```
 *
 * **Stable callbacks for performance:**
 * ```tsx
 * const onScrollRef = useLatest(onScroll);
 * const handleScroll = useCallback(() => {
 *   onScrollRef.current?.(window.scrollY);
 * }, []); // Stable callback, always calls latest onScroll
 * ```
 *
 * ### Anti-patterns
 *
 * **Don't use for render values** (refs don't trigger re-renders):
 * ```tsx
 * // BAD
 * return <div>{countRef.current}</div>;
 * // GOOD
 * return <div>{count}</div>;
 * ```
 *
 * **Don't use when effect should re-run on change:**
 * ```tsx
 * // BAD - effect won't re-run when fetchData changes
 * useEffect(() => { fetchDataRef.current(); }, []);
 * // GOOD
 * useEffect(() => { fetchData(); }, [fetchData]);
 * ```
 *
 * **Don't pass ref.current to memoized children** (identity changes):
 * ```tsx
 * // BAD
 * <MemoizedInput onChange={onChangeRef.current} />
 * // GOOD
 * <MemoizedInput onChange={onChange} />
 * ```
 *
 * ### Implementation Notes
 *
 * Uses `useLayoutEffect` to update synchronously after DOM mutations:
 * - **Concurrent-safe**: Only commits values from renders that complete
 * - **Minimal timing gap**: Ref updated before most user code runs
 * - **React-compliant**: Ref mutations in effects, not during render
 *
 * Alternatives considered:
 * - `ref.current = value` in render: Not concurrent-safe
 * - `useEffect`: Larger timing gap (runs after paint)
 * - `useInsertionEffect`: Intended for CSS-in-JS libraries
 */
export function useLatest<T>(value: T): MutableRefObject<T> {
  const ref = useRef(value);

  // useLayoutEffect runs synchronously after DOM mutations but before paint.
  // This is concurrent-safe (won't write stale values from abandoned renders)
  // and follows React's guidelines for ref mutations (in effects, not render).
  useLayoutEffect(() => {
    ref.current = value;
  });

  return ref;
}
